// papaparse (~19 kB raw) is loaded on demand — it is only ever used inside
// export/import handlers, never during render, so it has no place in the eager
// bundle that the (eager) Results/HFW/CVC screens pull in via this module
// (2026-07 bundle-reduction follow-up).
const loadPapa = () => import('papaparse').then((m) => m.default);

export const SCHOOL_AUTHORIZATION_NOTICE = 'Use only with files your school/district has authorized you to import or export.';

const FORMULA_PREFIX_PATTERN = /^[=+\-@]/;

export const sanitizeCell = (value, maxLen = 120) => {
  if (value === null || value === undefined) return '';
  return value
    .toString()
    .replace(/<[^>]*>/g, '')
    .replace(/[<>"'`]/g, '')
    // eslint-disable-next-line no-control-regex
    .replace(/[\x00-\x1F\x7F]/g, '')
    .slice(0, maxLen)
    .trim();
};

export const protectCsvFormula = (value = '') => {
  const sanitized = value.toString().trim();
  return FORMULA_PREFIX_PATTERN.test(sanitized) ? `'${sanitized}` : sanitized;
};

export const safeCsvCell = (value, maxLen = 120) =>
  protectCsvFormula(sanitizeCell(value, maxLen));

export const splitStudentName = (fullName = '') => {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: '', lastName: '' };
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };
  return {
    firstName: parts.slice(0, -1).join(' '),
    lastName: parts[parts.length - 1],
  };
};

export const studentDisplayName = (student = {}) => {
  const fullName = student.name || [student.firstName, student.lastName].filter(Boolean).join(' ');
  return fullName.trim();
};

export const coerceDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value.toDate === 'function') return value.toDate();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const formatDateForCsv = (value) => {
  const date = coerceDate(value);
  if (!date) return '';
  return date.toISOString().slice(0, 10);
};

export const makeSafeFilenamePart = (value = '') =>
  safeCsvCell(value, 80).replace(/[^a-z0-9-_]+/gi, '_').replace(/^_+|_+$/g, '') || 'export';

export const downloadCsv = (filename, csvContent) => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const ASSESSMENT_EXPORT_FIELDS = [
  'localStudentId',
  'firstName',
  'lastName',
  'grade',
  'className',
  'assessmentType',
  'assessmentCategory',
  'quarter',
  'assessmentDate',
  'score',
  'totalPossible',
  'percentCorrect',
  'status',
  'manualEntry',
  // v3.85.0 (Sounds variants) — appended at the END so every existing column
  // keeps its position: the case a Letter/Sound run was given in
  // ('uppercase' | 'lowercase' | 'both') and the letters it preset as not
  // administered, comma-joined ("a,e,i,o,u" for a consonants-only run).
  // Empty for every older doc and for every other assessment family.
  'letter_case',
  'not_administered',
];

const getClassName = (classId, classes = {}) =>
  classes[classId]?.className || (classId?.includes('_') ? classId.split('_')[0] : classId) || '';

const getTotalPossible = (assessment = {}) =>
  assessment.totalPossible ??
  assessment.totalLetters ??
  assessment.totalWords ??
  (assessment.responses ? Object.keys(assessment.responses).length : '');

const getScore = (assessment = {}) => {
  if (assessment.correctCount === -1 || assessment.correctCount === 'N/A') return 'NA';
  return assessment.correctCount ?? '';
};

const getPercentCorrect = (assessment = {}) => {
  const score = getScore(assessment);
  const total = Number(getTotalPossible(assessment));
  if (score === 'NA' || score === '' || !total) return '';
  const numericScore = Number(score);
  if (Number.isNaN(numericScore)) return '';
  return ((numericScore / total) * 100).toFixed(1);
};

const resolveAssessmentType = (assessment, assessmentTypeLabel) => {
  if (typeof assessmentTypeLabel === 'function') return assessmentTypeLabel(assessment) || assessment.testType || '';
  if (assessmentTypeLabel && assessment.testType) return assessmentTypeLabel[assessment.testType] || assessment.testType;
  return assessment.testType || '';
};

export const buildAssessmentExport = ({
  students = [],
  classes = {},
  assessments = [],
  assessmentTypeLabel,
  defaultAssessmentCategory = '',
}) => {
  const studentById = students.reduce((acc, student) => {
    acc[student.id || student.studentId] = student;
    return acc;
  }, {});

  const rows = assessments
    .map(assessment => {
      const student = studentById[assessment.studentId];
      if (!student) return null;
      const fallbackName = splitStudentName(studentDisplayName(student));
      const valueMap = {
        localStudentId: student.localStudentId || '',
        firstName: student.firstName || fallbackName.firstName,
        lastName: student.lastName || fallbackName.lastName,
        grade: student.grade || '',
        className: getClassName(assessment.classId || student.classId, classes),
        assessmentType: resolveAssessmentType(assessment, assessmentTypeLabel),
        assessmentCategory: assessment.testCategory || defaultAssessmentCategory || '',
        quarter: assessment.quarter || '',
        assessmentDate: formatDateForCsv(assessment.timestamp),
        score: getScore(assessment),
        totalPossible: getTotalPossible(assessment),
        percentCorrect: getPercentCorrect(assessment),
        status: assessment.status || '',
        manualEntry: assessment.manualEntry ? 'yes' : 'no',
        letter_case: typeof assessment.letterCase === 'string' ? assessment.letterCase : '',
        not_administered: Array.isArray(assessment.notAdministered) ? assessment.notAdministered.join(',') : '',
      };
      return ASSESSMENT_EXPORT_FIELDS.reduce((row, field) => {
        row[field] = safeCsvCell(valueMap[field] ?? '');
        return row;
      }, {});
    })
    .filter(Boolean);

  return {
    fields: ASSESSMENT_EXPORT_FIELDS,
    rows,
    rowCount: rows.length,
  };
};

export const unparseAssessmentCsv = async (exportData) => {
  const Papa = await loadPapa();
  return Papa.unparse({
    fields: exportData.fields,
    data: exportData.rows,
  });
};
